import { Button } from '@flowus/common/components/button';
import { useApiState } from '@flowus/common/hooks/use-api-state';
import { useClipboard } from '@flowus/common/hooks/use-clipboard';
import { useOpenModal } from '@flowus/common/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { Modals } from '@flowus/shared';
import type { FC } from 'react';
import { Input } from 'src/common/components/input';
import { publicLink } from 'src/common/const';
import { request } from 'src/common/request/request';
import { HelperIconBox } from 'src/components/helper-icon';
import { useCurrentUser } from 'src/hooks/user';
import { SearchParams, ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { getLocationOrigin } from 'src/utils/location-utils';
import { SettingModalCommon } from '../common';
import { OrderRecordModal } from './order-record-modal';
import { Icon } from 'src/common/components/icon';

enum CopyType {
  link,
  code,
}

interface Props {}

/** 邀请好友 */
export const InvitationInfo: FC<Props> = () => {
  const { t } = useAppTranslation();
  const currentUser = useCurrentUser();
  const openModal = useOpenModal();
  const linkCopyId = useClipboard();
  const codeCopyId = useClipboard();
  const [userOrder] = useApiState({
    // index, size, type
    getApi: () => request.infra.distributionAlliancePayRecords(1, 1, 1),
  });
  const [pageOrder] = useApiState({
    getApi: () => request.infra.distributionAlliancePayRecords(1, 1, 0),
  });

  const link = `${getLocationOrigin()}${ViewPath.login}?${SearchParams.inviteCode}=${
    currentUser.inviteCode
  }`;

  const handleCopy = (type: CopyType) => {
    bizTracker.event(type === CopyType.link ? 'invite_copylink' : 'invite_copycode');
  };

  const openRecordModal = (type: 'user' | 'page') => {
    openModal.modal({
      modalId: Modals.ORDER_RECORD_MODAL,
      content: () => <OrderRecordModal type={type} />,
    });
  };

  return (
    <div className="space-y-2.5">
      <div hidden className="py-4 border-b flex items-center justify-between">
        <div>{t('views.settings.profit.space_member_commission')}</div>
        <div
          className="space-x-1 flex items-center animate-click"
          onClick={() => openRecordModal('user')}
        >
          <span>
            {t('views.settings.profit.cumulative_space_commission')}：
            <span className="text-t2-medium">
              {userOrder?.total} {t('common.orders')}
            </span>
          </span>
          <SettingModalCommon.Arrow />
        </div>
      </div>
      <div hidden className="relative rounded border px-4 pt-10 pb-5">
        <span className="absolute top-0 left-0 text-t3 bg-active_color text-white px-1.5 py-px rounded-sm">
          {t('views.settings.profit.method_one')}
        </span>
        <div className="mb-2.5">{t('views.settings.profit.invite_new_users')}</div>
        <Input
          value={`${location.origin}${ViewPath.login}?${SearchParams.inviteCode}=${currentUser.inviteCode}`}
          inputClassName="h-8 text-black"
          className="overflow-hidden"
          readonly
          addonAfter={
            <>
              <div className="h-8 bg-grey6 w-px" />
              <Button
                id={linkCopyId}
                className="border-none rounded-none"
                onClick={() => handleCopy(CopyType.link)}
                data-clipboard-text={link}
              >
                {t('common.components.link_widget.copy_link')}
              </Button>
              <div className="h-8 bg-grey6 w-px" />
              <Button
                id={codeCopyId}
                className="border-none rounded-none"
                onClick={() => handleCopy(CopyType.code)}
                data-clipboard-text={currentUser.inviteCode}
              >
                {t('views.settings.profit.copy_code')}
              </Button>
            </>
          }
        />
      </div>
      <div hidden className="relative rounded border px-4 pt-10 pb-5">
        <span className="absolute top-0 left-0 text-t3 bg-black-base text-white px-1.5 py-px rounded-sm">
          {t('views.settings.profit.method_two')}
        </span>
        <div className="mb-2.5">{t('views.settings.profit.share_any_document')}</div>
        <HelperIconBox
          className="text-grey3"
          beforeIcon
          linkContainer
          link={publicLink.helperShare}
        >
          {t('views.settings.profit.how_to_share_page')}
        </HelperIconBox>
      </div>
      <div className="py-4 border-b flex items-center justify-between">
        <div>{t('views.settings.profit.paid_subscription_commission')}</div>
        <div
          className="space-x-1 flex items-center animate-click"
          onClick={() => openRecordModal('page')}
        >
          <span>
            {t('views.settings.profit.cumulative_paid_commission')}：
            <span className="text-t2-medium">
              {pageOrder?.total} {t('common.orders')}
            </span>
          </span>
          <Icon className="text-grey3" name="IcArrowDateNext" size="small" />
        </div>
      </div>
      <div className="relative rounded border px-4 pt-10 pb-5">
        <span className="absolute top-0 left-0 text-t3 bg-active_color text-white px-1.5 py-px rounded-sm">
          {t('views.settings.profit.method_one')}
        </span>
        <div className="mb-2.5 space-x-2 justify-between">
          <span>{t('views.settings.profit.friend_paid_subscription')}</span>
          <a
            href={publicLink.subscribeCenter}
            target="_blank"
            className="text-active_color animate-click"
          >
            {t('views.settings.profit.go_to_knowledge_library')}
          </a>
        </div>
        <HelperIconBox
          className="text-grey3 inline-flex items-center"
          beforeIcon
          linkContainer
          link={publicLink.rebateRule}
        >
          {t('views.settings.profit.what_is_profit_sharing')}
        </HelperIconBox>
      </div>
    </div>
  );
};
